import React, { useState, useEffect } from 'react';
import { adminApi } from '../lib/adminAuth';
import { formatDateToLocal, getTodayLocal } from '../lib/dateUtils';
import { toast } from 'sonner';
import {
  FileText, Download, Filter, Calendar, Users, TrendingUp,
  Receipt, Clock, ChevronDown, RefreshCw, X, Search,
  BarChart2, PieChart, ArrowUpDown, Shield, CheckCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import EmptyState from './EmptyState';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { SearchableSelect } from './ui/searchable-select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { HeaderSkeleton, MetricCardsSkeleton, TableSkeleton } from './skeletons/Skeletons';

// Format currency in Indian format
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

// Date preset helper for reports
const getReportDateRange = (preset) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const fyStartYear = month >= 3 ? year : year - 1;
  const pad = (n) => String(n).padStart(2, '0');

  switch (preset) {
    case 'current_fy':
      return { from: `${fyStartYear}-04-01`, to: `${year}-${pad(month + 1)}-${pad(now.getDate())}` };
    case 'current_cy':
      return { from: `${year}-01-01`, to: `${year}-${pad(month + 1)}-${pad(now.getDate())}` };
    case 'last_month': {
      const lm = month === 0 ? 11 : month - 1;
      const lmYear = month === 0 ? year - 1 : year;
      const lastDay = new Date(lmYear, lm + 1, 0).getDate();
      return { from: `${lmYear}-${pad(lm + 1)}-01`, to: `${lmYear}-${pad(lm + 1)}-${pad(lastDay)}` };
    }
    case 'last_quarter': {
      const cq = Math.floor(month / 3);
      const lq = cq === 0 ? 3 : cq - 1;
      const lqYear = cq === 0 ? year - 1 : year;
      const lqStartMonth = lq * 3;
      const lqEndMonth = lqStartMonth + 2;
      const lqLastDay = new Date(lqYear, lqEndMonth + 1, 0).getDate();
      return { from: `${lqYear}-${pad(lqStartMonth + 1)}-01`, to: `${lqYear}-${pad(lqEndMonth + 1)}-${pad(lqLastDay)}` };
    }
    case 'last_fy':
      return { from: `${fyStartYear - 1}-04-01`, to: `${fyStartYear}-03-31` };
    case 'all_time':
      return { from: '', to: '' };
    default:
      return { from: '', to: '' };
  }
};

// Date Preset Selector component
function DatePresetSelector({ value, onChange, showCustom = true }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-48" data-testid="date-preset-select">
        <SelectValue placeholder="Select period" />
      </SelectTrigger>
      <SelectContent className="bg-white">
        <SelectItem value="current_fy">Current FY (Apr–Mar)</SelectItem>
        <SelectItem value="current_cy">Current Calendar Year</SelectItem>
        <SelectItem value="last_month">Last Month</SelectItem>
        <SelectItem value="last_quarter">Last Quarter</SelectItem>
        <SelectItem value="last_fy">Last FY</SelectItem>
        <SelectItem value="all_time">All Time</SelectItem>
        {showCustom && <SelectItem value="custom">Custom Range</SelectItem>}
      </SelectContent>
    </Select>
  );
}

// Report Type Card
function ReportTypeCard({ title, description, icon: Icon, color, onClick }) {
  const colorClasses = {
    blue: 'hover:border-blue-500 hover:bg-blue-50/50',
    green: 'hover:border-emerald-500 hover:bg-emerald-50/50',
    purple: 'hover:border-purple-500 hover:bg-purple-50/50',
    orange: 'hover:border-orange-500 hover:bg-orange-50/50',
    red: 'hover:border-red-500 hover:bg-red-50/50',
    teal: 'hover:border-teal-500 hover:bg-teal-50/50',
  };

  const iconColors = {
    blue: 'text-blue-500 bg-blue-100',
    green: 'text-emerald-500 bg-emerald-100',
    purple: 'text-purple-500 bg-purple-100',
    orange: 'text-orange-500 bg-orange-100',
    red: 'text-red-500 bg-red-100',
    teal: 'text-teal-500 bg-teal-100',
  };

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-5 rounded-xl border-2 border-white/40 bg-white transition-all duration-200 ${colorClasses[color]}`}
      data-testid={`report-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-xl ${iconColors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <h3 className="font-semibold text-[#1a1a2e]">{title}</h3>
          <p className="text-sm text-neutral-500 mt-1">{description}</p>
        </div>
      </div>
    </button>
  );
}

// Revenue Report Component
function RevenueReport({ onBack }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [groupBy, setGroupBy] = useState('month');
  const [datePreset, setDatePreset] = useState('current_fy');
  const defaultRange = getReportDateRange('current_fy');
  const [fromDate, setFromDate] = useState(defaultRange.from);
  const [toDate, setToDate] = useState(defaultRange.to);

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      const range = getReportDateRange(preset);
      setFromDate(range.from);
      setToDate(range.to);
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = { group_by: groupBy };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const response = await adminApi.get('/api/admin/accounting/reports/revenue', { params });
      setData(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [fromDate, toDate, groupBy]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={onBack} size="sm">
          ← Back
        </Button>
        <div className="flex items-center gap-2">
          <DatePresetSelector value={datePreset} onChange={handlePresetChange} />
          {datePreset === 'custom' && (
            <>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40" />
              <span className="text-neutral-500">to</span>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40" />
            </>
          )}
        </div>
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Monthly</SelectItem>
            <SelectItem value="quarter">Quarterly</SelectItem>
            <SelectItem value="client">By Client</SelectItem>
            <SelectItem value="type">By Type</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={fetchReport} disabled={loading} size="sm">
          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      {data && (
        <div className="soft-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/40 border-b border-white/40">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Period/Category</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">Base Revenue</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">GST</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">TDS</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">Total Receivable</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">Received</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-neutral-700">Count</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/30">
                {data.data?.map((row, i) => (
                  <tr key={i} className="hover:bg-white/60">
                    <td className="px-4 py-3 font-medium text-[#1a1a2e]">{row.label}</td>
                    <td className="px-4 py-3 text-right text-neutral-700">{formatCurrency(row.subtotal)}</td>
                    <td className="px-4 py-3 text-right text-blue-600">{formatCurrency(row.gst)}</td>
                    <td className="px-4 py-3 text-right text-orange-600">{formatCurrency(row.tds)}</td>
                    <td className="px-4 py-3 text-right text-emerald-600 font-medium">{formatCurrency(row.total_invoiced)}</td>
                    <td className="px-4 py-3 text-right text-teal-600">{formatCurrency(row.received)}</td>
                    <td className="px-4 py-3 text-center text-neutral-500">{row.count}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-neutral-100 border-t-2 border-white/40">
                <tr className="font-bold">
                  <td className="px-4 py-3 text-[#1a1a2e]">TOTAL</td>
                  <td className="px-4 py-3 text-right text-[#1a1a2e]">{formatCurrency(data.totals?.subtotal)}</td>
                  <td className="px-4 py-3 text-right text-blue-700">{formatCurrency(data.totals?.gst)}</td>
                  <td className="px-4 py-3 text-right text-orange-700">{formatCurrency(data.totals?.tds)}</td>
                  <td className="px-4 py-3 text-right text-emerald-700">{formatCurrency(data.totals?.total_invoiced)}</td>
                  <td className="px-4 py-3 text-right text-teal-700">{formatCurrency(data.totals?.received)}</td>
                  <td className="px-4 py-3 text-center text-neutral-700">{data.totals?.count}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Aged Receivables Report
function AgedReceivablesReport({ onBack }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [asOfDate, setAsOfDate] = useState(() => getTodayLocal());

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/api/admin/accounting/reports/aged-receivables', {
        params: { as_of_date: asOfDate }
      });
      setData(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [asOfDate]);

  const bucketLabels = {
    current: 'Current',
    days_1_30: '1-30 Days',
    days_31_60: '31-60 Days',
    days_61_90: '61-90 Days',
    over_90: '90+ Days'
  };

  const bucketColors = {
    current: 'bg-emerald-100 text-emerald-700',
    days_1_30: 'bg-blue-100 text-blue-700',
    days_31_60: 'bg-amber-100 text-amber-700',
    days_61_90: 'bg-orange-100 text-orange-700',
    over_90: 'bg-red-100 text-red-700'
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={onBack} size="sm">
          ← Back
        </Button>
        <div className="flex items-center gap-2">
          <span className="text-sm text-neutral-500">As of:</span>
          <Input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} className="w-40" />
        </div>
        <Button onClick={fetchReport} disabled={loading} size="sm">
          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {Object.entries(data.summary || {}).map(([key, value]) => (
              <div key={key} className={`p-4 rounded-xl ${bucketColors[key]}`}>
                <p className="text-xs font-medium opacity-80">{bucketLabels[key]}</p>
                <p className="text-xl font-bold mt-1">{formatCurrency(value)}</p>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-4 rounded-xl flex justify-between items-center">
            <span className="font-medium">Total Outstanding</span>
            <span className="text-2xl font-bold">{formatCurrency(data.total_outstanding)}</span>
          </div>

          {/* Details Table */}
          <div className="soft-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/40 border-b border-white/40">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Invoice</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Client</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Date</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">Total</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">Outstanding</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-neutral-700">Days</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-neutral-700">Age</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/30">
                  {data.details?.map((row, i) => (
                    <tr key={i} className="hover:bg-white/60">
                      <td className="px-4 py-3 font-medium text-[#1a1a2e]">{row.invoice_number || '-'}</td>
                      <td className="px-4 py-3 text-neutral-700">{row.client_name}</td>
                      <td className="px-4 py-3 text-neutral-500 text-sm">{row.invoice_date}</td>
                      <td className="px-4 py-3 text-right text-neutral-700">{formatCurrency(row.total)}</td>
                      <td className="px-4 py-3 text-right font-medium text-[#1a1a2e]">{formatCurrency(row.outstanding)}</td>
                      <td className="px-4 py-3 text-center text-neutral-500">{row.days_overdue}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${bucketColors[row.bucket]}`}>
                          {bucketLabels[row.bucket]}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// GST Summary Report
function GSTSummaryReport({ onBack }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/api/admin/accounting/reports/gst-summary', {
        params: { period }
      });
      setData(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [period]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={onBack} size="sm">
          ← Back
        </Button>
        <div className="flex items-center gap-2">
          <span className="text-sm text-neutral-500">Period:</span>
          <Input 
            type="month" 
            value={period} 
            onChange={(e) => setPeriod(e.target.value)} 
            className="w-40" 
          />
        </div>
        <Button onClick={fetchReport} disabled={loading} size="sm">
          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      {data && (
        <>
          {/* Period Header */}
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white p-5 rounded-xl">
            <h3 className="font-semibold text-lg">{data.period_name}</h3>
            <p className="text-sm text-white/80 mt-1">GST Summary for GSTR-1 / GSTR-3B</p>
          </div>

          {/* Totals Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-xl border border-white/40">
              <p className="text-xs text-neutral-500 mb-1">Taxable Value</p>
              <p className="text-xl font-bold text-[#1a1a2e]">{formatCurrency(data.totals?.taxable_value)}</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-white/40">
              <p className="text-xs text-neutral-500 mb-1">CGST + SGST</p>
              <p className="text-xl font-bold text-blue-600">
                {formatCurrency((data.totals?.cgst || 0) + (data.totals?.sgst || 0))}
              </p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-white/40">
              <p className="text-xs text-neutral-500 mb-1">IGST</p>
              <p className="text-xl font-bold text-purple-600">{formatCurrency(data.totals?.igst)}</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-white/40">
              <p className="text-xs text-neutral-500 mb-1">Total GST</p>
              <p className="text-xl font-bold text-emerald-600">{formatCurrency(data.totals?.total_gst)}</p>
            </div>
          </div>

          {/* Rate-wise Summary */}
          <div className="glass-card p-5">
            <h4 className="font-semibold text-[#1a1a2e] mb-4">Tax Rate-wise Summary</h4>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/40 border-b border-white/40">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-semibold text-neutral-700">Rate</th>
                    <th className="px-4 py-2 text-right text-sm font-semibold text-neutral-700">Taxable</th>
                    <th className="px-4 py-2 text-right text-sm font-semibold text-neutral-700">CGST</th>
                    <th className="px-4 py-2 text-right text-sm font-semibold text-neutral-700">SGST</th>
                    <th className="px-4 py-2 text-right text-sm font-semibold text-neutral-700">IGST</th>
                    <th className="px-4 py-2 text-center text-sm font-semibold text-neutral-700">Invoices</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/30">
                  {data.rate_summary?.map((row, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2 font-medium text-[#1a1a2e]">{row.rate}%</td>
                      <td className="px-4 py-2 text-right text-neutral-700">{formatCurrency(row.taxable)}</td>
                      <td className="px-4 py-2 text-right text-neutral-600">{formatCurrency(row.cgst)}</td>
                      <td className="px-4 py-2 text-right text-neutral-600">{formatCurrency(row.sgst)}</td>
                      <td className="px-4 py-2 text-right text-neutral-600">{formatCurrency(row.igst)}</td>
                      <td className="px-4 py-2 text-center text-neutral-500">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* State-wise Summary */}
          <div className="glass-card p-5">
            <h4 className="font-semibold text-[#1a1a2e] mb-4">State-wise Summary</h4>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/40 border-b border-white/40">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-semibold text-neutral-700">State</th>
                    <th className="px-4 py-2 text-right text-sm font-semibold text-neutral-700">Taxable</th>
                    <th className="px-4 py-2 text-right text-sm font-semibold text-neutral-700">Total GST</th>
                    <th className="px-4 py-2 text-center text-sm font-semibold text-neutral-700">Invoices</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/30">
                  {data.state_summary?.map((row, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2 font-medium text-[#1a1a2e]">{row.state}</td>
                      <td className="px-4 py-2 text-right text-neutral-700">{formatCurrency(row.taxable)}</td>
                      <td className="px-4 py-2 text-right text-neutral-600">{formatCurrency(row.total)}</td>
                      <td className="px-4 py-2 text-center text-neutral-500">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* B2B Invoice List */}
          <div className="glass-card p-5">
            <h4 className="font-semibold text-[#1a1a2e] mb-4">B2B Invoices ({data.b2b_invoices?.length || 0})</h4>
            <div className="overflow-x-auto max-h-96">
              <table className="w-full">
                <thead className="bg-white/40 border-b border-white/40 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-neutral-700">Invoice #</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-neutral-700">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-neutral-700">Client</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-neutral-700">GSTIN</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-neutral-700">Taxable</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-neutral-700">GST</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-neutral-700">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/30">
                  {data.b2b_invoices?.map((inv, i) => (
                    <tr key={i} className="hover:bg-white/60 text-sm">
                      <td className="px-3 py-2 font-medium text-[#1a1a2e]">{inv.invoice_number || '-'}</td>
                      <td className="px-3 py-2 text-neutral-500">{inv.invoice_date || '-'}</td>
                      <td className="px-3 py-2 text-neutral-700 truncate max-w-[150px]">{inv.client_name}</td>
                      <td className="px-3 py-2 text-neutral-500 font-mono text-xs">{inv.gstin || '-'}</td>
                      <td className="px-3 py-2 text-right text-neutral-700">{formatCurrency(inv.taxable_value)}</td>
                      <td className="px-3 py-2 text-right text-blue-600">{formatCurrency(inv.total_gst)}</td>
                      <td className="px-3 py-2 text-right font-medium text-[#1a1a2e]">{formatCurrency(inv.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// TDS Summary Report
function TDSSummaryReport({ onBack }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 11);
    return formatDateToLocal(d);
  });
  const [toDate, setToDate] = useState(() => getTodayLocal());

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/api/admin/accounting/reports/tds-summary', {
        params: { from_date: fromDate, to_date: toDate }
      });
      setData(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [fromDate, toDate]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={onBack} size="sm">
          ← Back
        </Button>
        <div className="flex items-center gap-2">
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40" />
          <span className="text-neutral-500">to</span>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40" />
        </div>
        <Button onClick={fetchReport} disabled={loading} size="sm">
          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      {data && (
        <>
          {/* Totals */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-5 rounded-xl">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-white/80">Total TDS Deducted</p>
                <p className="text-3xl font-bold">{formatCurrency(data.totals?.tds)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-white/80">From Taxable Amount</p>
                <p className="text-xl font-semibold">{formatCurrency(data.totals?.taxable)}</p>
              </div>
            </div>
          </div>

          {/* Client-wise Summary */}
          <div className="glass-card p-5">
            <h4 className="font-semibold text-[#1a1a2e] mb-4">Deductor-wise Summary</h4>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/40 border-b border-white/40">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-semibold text-neutral-700">Deductor (Client)</th>
                    <th className="px-4 py-2 text-left text-sm font-semibold text-neutral-700">PAN</th>
                    <th className="px-4 py-2 text-right text-sm font-semibold text-neutral-700">Taxable</th>
                    <th className="px-4 py-2 text-right text-sm font-semibold text-neutral-700">TDS</th>
                    <th className="px-4 py-2 text-center text-sm font-semibold text-neutral-700">Invoices</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/30">
                  {data.client_summary?.map((row, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2 font-medium text-[#1a1a2e]">{row.name}</td>
                      <td className="px-4 py-2 text-neutral-500 font-mono text-sm">{row.pan || '-'}</td>
                      <td className="px-4 py-2 text-right text-neutral-700">{formatCurrency(row.taxable)}</td>
                      <td className="px-4 py-2 text-right font-medium text-orange-600">{formatCurrency(row.tds)}</td>
                      <td className="px-4 py-2 text-center text-neutral-500">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="glass-card p-5">
            <h4 className="font-semibold text-[#1a1a2e] mb-4">Invoice Details ({data.details?.length || 0})</h4>
            <div className="overflow-x-auto max-h-80">
              <table className="w-full">
                <thead className="bg-white/40 border-b border-white/40 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-neutral-700">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-neutral-700">Invoice</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-neutral-700">Client</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-neutral-700">Taxable</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-neutral-700">Rate</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-neutral-700">TDS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/30">
                  {data.details?.map((row, i) => (
                    <tr key={i} className="hover:bg-white/60 text-sm">
                      <td className="px-3 py-2 text-neutral-500">{row.date}</td>
                      <td className="px-3 py-2 font-medium text-[#1a1a2e]">{row.invoice_number}</td>
                      <td className="px-3 py-2 text-neutral-700">{row.client_name}</td>
                      <td className="px-3 py-2 text-right text-neutral-700">{formatCurrency(row.taxable_amount)}</td>
                      <td className="px-3 py-2 text-center text-neutral-500">{row.tds_rate}%</td>
                      <td className="px-3 py-2 text-right font-medium text-orange-600">{formatCurrency(row.tds_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Client Statement Report
function ClientStatementReport({ onBack, initialClientId }) {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(initialClientId || 'all');
  const [loading, setLoading] = useState(true);
  const [allClientsData, setAllClientsData] = useState([]);
  const [singleClientData, setSingleClientData] = useState(null);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 12);
    return formatDateToLocal(d);
  });
  const [toDate, setToDate] = useState(() => getTodayLocal());
  const [expandedClient, setExpandedClient] = useState(null);

  // Fetch clients on mount
  useEffect(() => {
    adminApi.get('/api/admin/clients').then(res => {
      // Include all clients that are not explicitly archived
      const availableClients = (res.data || []).filter(c => c.status !== 'archived');
      setClients(availableClients);
    }).catch(() => {});
  }, []);

  // Auto-fetch data when clients load or filters change
  useEffect(() => {
    if (clients.length > 0) {
      fetchReport();
    }
  }, [clients, selectedClient, fromDate, toDate]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      if (selectedClient === 'all') {
        // Fetch statements for all clients in parallel
        const promises = clients.map(c => 
          adminApi.get(`/api/admin/accounting/reports/client-statement/${c._id}`, { params })
            .then(res => ({ ...res.data, client_id: c._id }))
            .catch(() => null)
        );
        const results = await Promise.all(promises);
        const validResults = results.filter(r => r && (r.summary?.total_invoiced > 0 || r.summary?.closing_balance !== 0));
        setAllClientsData(validResults);
        setSingleClientData(null);
      } else {
        const response = await adminApi.get(`/api/admin/accounting/reports/client-statement/${selectedClient}`, { params });
        setSingleClientData(response.data);
        setAllClientsData([]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load statement');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals for all clients view
  const allClientsTotals = allClientsData.reduce((acc, d) => ({
    total_invoiced: acc.total_invoiced + (d.summary?.total_invoiced || 0),
    total_received: acc.total_received + (d.summary?.total_received || 0),
    closing_balance: acc.closing_balance + (d.summary?.closing_balance || 0)
  }), { total_invoiced: 0, total_received: 0, closing_balance: 0 });

  return (
    <div className="space-y-4">
      {/* Header with Filters */}
      <div className="glass-card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={onBack} size="sm" data-testid="back-btn">
            ← Back
          </Button>
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger className="w-64" data-testid="client-filter">
              <SelectValue placeholder="All Clients" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-teal-600" />
                  All Clients ({clients.length})
                </div>
              </SelectItem>
              {clients.map(c => (
                <SelectItem key={c._id} value={c._id}>
                  {c.company_name || c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-neutral-400" />
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-36" />
            <span className="text-neutral-400">to</span>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-36" />
          </div>
          <Button onClick={fetchReport} disabled={loading} variant="outline" size="sm">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-teal-500" />
        </div>
      ) : selectedClient === 'all' ? (
        /* All Clients Summary View */
        <>
          {/* Grand Totals */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-[#3366FF] to-[#3366FF] p-5 rounded-xl text-white">
              <p className="text-sm font-medium text-blue-100">Total Billed</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(allClientsTotals.total_invoiced)}</p>
              <p className="text-xs text-blue-200 mt-2">{allClientsData.length} clients with activity</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-5 rounded-xl text-white">
              <p className="text-sm font-medium text-emerald-100">Total Received</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(allClientsTotals.total_received)}</p>
              <p className="text-xs text-emerald-200 mt-2">Collection rate: {allClientsTotals.total_invoiced > 0 ? Math.round((allClientsTotals.total_received / allClientsTotals.total_invoiced) * 100) : 0}%</p>
            </div>
            <div className={`p-5 rounded-xl text-white ${allClientsTotals.closing_balance > 0 ? 'bg-gradient-to-br from-red-500 to-red-600' : 'bg-gradient-to-br from-gray-500 to-gray-600'}`}>
              <p className="text-sm font-medium opacity-90">Total Outstanding</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(allClientsTotals.closing_balance)}</p>
              <p className="text-xs opacity-80 mt-2">{allClientsData.filter(d => d.summary?.closing_balance > 0).length} clients with dues</p>
            </div>
          </div>

          {/* Clients List */}
          <div className="soft-card overflow-hidden">
            <div className="px-4 py-3 bg-white/40 border-b border-white/40">
              <h3 className="font-semibold text-[#1a1a2e]">Client Statements</h3>
            </div>
            <div className="divide-y divide-white/30">
              {allClientsData.length === 0 ? (
                <div className="p-8 text-center text-neutral-500">
                  <Receipt className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
                  <p>No transactions found for the selected period</p>
                </div>
              ) : (
                allClientsData.map((clientData, idx) => (
                  <div key={clientData.client_id || idx} className="hover:bg-white/60 transition-colors">
                    {/* Client Row - Clickable */}
                    <button
                      onClick={() => setExpandedClient(expandedClient === clientData.client_id ? null : clientData.client_id)}
                      className="w-full px-4 py-4 flex items-center justify-between text-left"
                      data-testid={`client-row-${clientData.client_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold">
                          {clientData.client?.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-semibold text-[#1a1a2e]">{clientData.client?.name}</p>
                          <p className="text-xs text-neutral-500">{clientData.client?.gstin || 'No GSTIN'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-xs text-neutral-500">Invoiced</p>
                          <p className="font-semibold text-[#1a1a2e]">{formatCurrency(clientData.summary?.total_invoiced)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-neutral-500">Received</p>
                          <p className="font-semibold text-emerald-600">{formatCurrency(clientData.summary?.total_received)}</p>
                        </div>
                        <div className="text-right min-w-[100px]">
                          <p className="text-xs text-neutral-500">Balance</p>
                          <p className={`font-bold ${clientData.summary?.closing_balance > 0 ? 'text-red-600' : 'text-neutral-600'}`}>
                            {formatCurrency(clientData.summary?.closing_balance)}
                          </p>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${expandedClient === clientData.client_id ? 'rotate-180' : ''}`} />
                      </div>
                    </button>

                    {/* Expanded Transactions */}
                    {expandedClient === clientData.client_id && clientData.transactions?.length > 0 && (
                      <div className="px-4 pb-4">
                        <div className="bg-white/40 rounded-xl overflow-hidden border border-white/40">
                          <table className="w-full text-sm">
                            <thead className="bg-neutral-100">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-neutral-600">Date</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-neutral-600">Type</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-neutral-600">Reference</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-neutral-600">Debit</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-neutral-600">Credit</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-neutral-600">Balance</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-white/30 bg-white">
                              {clientData.transactions.map((t, i) => (
                                <tr key={i} className="hover:bg-white/60">
                                  <td className="px-3 py-2 text-neutral-500">{t.date}</td>
                                  <td className="px-3 py-2">
                                    <span className="px-2 py-0.5 text-xs font-medium rounded bg-neutral-100 text-neutral-700 capitalize">{t.type}</span>
                                  </td>
                                  <td className="px-3 py-2 font-medium text-[#1a1a2e]">{t.reference || '-'}</td>
                                  <td className="px-3 py-2 text-right text-neutral-700">{t.debit > 0 ? formatCurrency(t.debit) : '-'}</td>
                                  <td className="px-3 py-2 text-right text-emerald-600">{t.credit > 0 ? formatCurrency(t.credit) : '-'}</td>
                                  <td className="px-3 py-2 text-right font-medium text-[#1a1a2e]">{formatCurrency(t.balance)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      ) : singleClientData && (
        /* Single Client View */
        <>
          {/* Client Header */}
          <div className="glass-card p-5">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-bold text-[#1a1a2e]">{singleClientData.client?.name}</h3>
                <p className="text-sm text-neutral-500 mt-1">{singleClientData.client?.address || 'No address'}</p>
                {singleClientData.client?.gstin && (
                  <p className="text-xs text-neutral-400 mt-1">GSTIN: {singleClientData.client.gstin}</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm text-neutral-500">Period</p>
                <p className="font-medium">{singleClientData.period?.from} to {singleClientData.period?.to}</p>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-xl">
              <p className="text-xs text-blue-600 font-medium">Total Billed</p>
              <p className="text-2xl font-bold text-blue-700">{formatCurrency(singleClientData.summary?.total_invoiced)}</p>
            </div>
            <div className="bg-emerald-50 p-4 rounded-xl">
              <p className="text-xs text-emerald-600 font-medium">Total Received</p>
              <p className="text-2xl font-bold text-emerald-700">{formatCurrency(singleClientData.summary?.total_received)}</p>
            </div>
            <div className={`p-4 rounded-xl ${singleClientData.summary?.closing_balance > 0 ? 'bg-red-50' : 'bg-white/40'}`}>
              <p className={`text-xs font-medium ${singleClientData.summary?.closing_balance > 0 ? 'text-red-600' : 'text-neutral-600'}`}>
                Closing Balance
              </p>
              <p className={`text-2xl font-bold ${singleClientData.summary?.closing_balance > 0 ? 'text-red-700' : 'text-neutral-700'}`}>
                {formatCurrency(singleClientData.summary?.closing_balance)}
              </p>
            </div>
          </div>

          {/* Transactions */}
          <div className="soft-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/40 border-b border-white/40">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Reference</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Description</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">Debit</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">Credit</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/30">
                  {singleClientData.transactions?.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-neutral-500">
                        No transactions found for this period
                      </td>
                    </tr>
                  ) : singleClientData.transactions?.map((t, i) => (
                    <tr key={i} className="hover:bg-white/60">
                      <td className="px-4 py-3 text-neutral-500 text-sm">{t.date}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 text-xs font-medium rounded bg-neutral-100 text-neutral-700 capitalize">
                          {t.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-[#1a1a2e]">{t.reference || '-'}</td>
                      <td className="px-4 py-3 text-neutral-600 text-sm">{t.description}</td>
                      <td className="px-4 py-3 text-right text-neutral-700">{t.debit > 0 ? formatCurrency(t.debit) : '-'}</td>
                      <td className="px-4 py-3 text-right text-emerald-600">{t.credit > 0 ? formatCurrency(t.credit) : '-'}</td>
                      <td className="px-4 py-3 text-right font-medium text-[#1a1a2e]">{formatCurrency(t.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============ PROFIT & LOSS REPORT COMPONENT ============
function ProfitLossReport({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState('current_fy');
  const defaultRange = getReportDateRange('current_fy');
  const [fromDate, setFromDate] = useState(defaultRange.from);
  const [toDate, setToDate] = useState(defaultRange.to);
  const [comparePrevious, setComparePrevious] = useState(false);

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      const range = getReportDateRange(preset);
      setFromDate(range.from);
      setToDate(range.to);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (comparePrevious) params.append('compare_previous', 'true');
      const response = await adminApi.get(`/api/admin/accounting/reports/profit-loss?${params}`);
      setData(response.data);
    } catch (err) {
      toast.error('Failed to load P&L report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [fromDate, toDate, comparePrevious]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="pnl-report-loading">
        <HeaderSkeleton withActions={false} />
        <MetricCardsSkeleton count={4} />
        <TableSkeleton rows={8} columns={4} />
      </div>
    );
  }

  if (!data) return null;

  const { revenue, cost_of_services, gross_profit, operating_expenses, net_profit, tax_info, monthly_breakdown, comparison, period } = data;

  return (
    <div data-testid="profit-loss-report" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="pl-back-btn">
            <X className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h2 className="text-xl font-bold text-[#1a1a2e]">Profit & Loss Statement</h2>
            <p className="text-sm text-neutral-500">{period.from} to {period.to} ({period.months} months)</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <DatePresetSelector value={datePreset} onChange={handlePresetChange} />
          {datePreset === 'custom' && (
            <>
              <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-36 text-sm" placeholder="From" />
              <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-36 text-sm" placeholder="To" />
            </>
          )}
          <label className="flex items-center gap-1.5 text-sm text-neutral-600 cursor-pointer">
            <input type="checkbox" checked={comparePrevious} onChange={e => setComparePrevious(e.target.checked)} className="rounded" />
            Compare
          </label>
          <Button size="sm" onClick={fetchData} data-testid="pl-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* P&L Statement */}
      <div className="glass-card shadow-soft overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-white/40 border-b border-white/40">
              <th className="px-6 py-3 text-left text-sm font-semibold text-neutral-700">Particulars</th>
              <th className="px-6 py-3 text-right text-sm font-semibold text-neutral-700">Amount</th>
              {comparison && <th className="px-6 py-3 text-right text-sm font-semibold text-neutral-500">Previous Period</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/30">
            {/* REVENUE SECTION */}
            <tr className="bg-emerald-50">
              <td colSpan={comparison ? 3 : 2} className="px-6 py-2 text-sm font-bold text-emerald-800 uppercase tracking-wide">Revenue</td>
            </tr>
            <tr>
              <td className="px-6 py-2.5 pl-10 text-sm text-neutral-700">Retainer Revenue</td>
              <td className="px-6 py-2.5 text-right text-sm font-medium text-[#1a1a2e]">{formatCurrency(revenue.retainer_revenue)}</td>
              {comparison && <td className="px-6 py-2.5 text-right text-sm text-neutral-400">-</td>}
            </tr>
            <tr>
              <td className="px-6 py-2.5 pl-10 text-sm text-neutral-700">Non-Retainer Revenue</td>
              <td className="px-6 py-2.5 text-right text-sm font-medium text-[#1a1a2e]">{formatCurrency(revenue.non_retainer_revenue)}</td>
              {comparison && <td className="px-6 py-2.5 text-right text-sm text-neutral-400">-</td>}
            </tr>
            <tr className="border-t border-white/40">
              <td className="px-6 py-2.5 pl-10 text-sm font-semibold text-[#1a1a2e]">Total Service Revenue ({revenue.invoice_count} invoices)</td>
              <td className="px-6 py-2.5 text-right text-sm font-bold text-[#1a1a2e]">{formatCurrency(revenue.service_revenue)}</td>
              {comparison && <td className="px-6 py-2.5 text-right text-sm font-medium text-neutral-500">{formatCurrency(comparison.revenue)}</td>}
            </tr>
            <tr>
              <td className="px-6 py-2.5 pl-10 text-sm text-red-600">Less: Credit Notes</td>
              <td className="px-6 py-2.5 text-right text-sm text-red-600">({formatCurrency(revenue.less_credit_notes)})</td>
              {comparison && <td className="px-6 py-2.5 text-right text-sm text-neutral-400">-</td>}
            </tr>
            <tr className="bg-emerald-50 border-t border-emerald-200">
              <td className="px-6 py-3 text-sm font-bold text-emerald-900">NET REVENUE</td>
              <td className="px-6 py-3 text-right text-sm font-bold text-emerald-900">{formatCurrency(revenue.net_revenue)}</td>
              {comparison && <td className="px-6 py-3 text-right text-sm font-medium text-neutral-500">{formatCurrency(comparison.revenue)}</td>}
            </tr>

            {/* COST OF SERVICES */}
            <tr className="bg-orange-50">
              <td colSpan={comparison ? 3 : 2} className="px-6 py-2 text-sm font-bold text-orange-800 uppercase tracking-wide">Cost of Services</td>
            </tr>
            <tr>
              <td className="px-6 py-2.5 pl-10 text-sm text-neutral-700">Direct Costs (Purchase Orders) [{cost_of_services.po_count} POs]</td>
              <td className="px-6 py-2.5 text-right text-sm font-medium text-[#1a1a2e]">{formatCurrency(cost_of_services.direct_costs)}</td>
              {comparison && <td className="px-6 py-2.5 text-right text-sm text-neutral-500">{formatCurrency(comparison.direct_costs)}</td>}
            </tr>

            {/* GROSS PROFIT */}
            <tr className="bg-blue-50 border-t-2 border-blue-200">
              <td className="px-6 py-3 text-sm font-bold text-blue-900">GROSS PROFIT <span className="text-xs font-normal text-blue-600 ml-1">({gross_profit.margin}% margin)</span></td>
              <td className="px-6 py-3 text-right text-sm font-bold text-blue-900">{formatCurrency(gross_profit.amount)}</td>
              {comparison && <td className="px-6 py-3 text-right text-sm font-medium text-neutral-500">{formatCurrency(comparison.gross_profit)}</td>}
            </tr>

            {/* OPERATING EXPENSES */}
            <tr className="bg-red-50">
              <td colSpan={comparison ? 3 : 2} className="px-6 py-2 text-sm font-bold text-red-800 uppercase tracking-wide">Operating Expenses</td>
            </tr>
            <tr>
              <td className="px-6 py-2.5 pl-10 text-sm text-neutral-700">Employee Salaries ({operating_expenses.employee_count} employees, {period.months} months)</td>
              <td className="px-6 py-2.5 text-right text-sm font-medium text-[#1a1a2e]">{formatCurrency(operating_expenses.salaries)}</td>
              {comparison && <td className="px-6 py-2.5 text-right text-sm text-neutral-500">{formatCurrency(comparison.salary_expense)}</td>}
            </tr>
            {/* Expense category breakdown */}
            {operating_expenses.expense_category_breakdown?.map((cat, idx) => (
              <tr key={idx}>
                <td className="px-6 py-2 pl-10 text-sm text-neutral-600">
                  {cat.category}
                  <span className="text-xs text-neutral-400 ml-1">({cat.count})</span>
                  <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${cat.type === 'fixed' ? 'bg-indigo-50 text-indigo-600' : 'bg-orange-50 text-orange-600'}`}>{cat.type}</span>
                </td>
                <td className="px-6 py-2 text-right text-sm text-neutral-700">{formatCurrency(cat.amount)}</td>
                {comparison && <td className="px-6 py-2 text-right text-sm text-neutral-400">-</td>}
              </tr>
            ))}
            {operating_expenses.other_expenses_total > 0 && (
              <tr className="border-t border-white/40">
                <td className="px-6 py-2 pl-10 text-sm font-semibold text-neutral-700">Subtotal Other Expenses ({operating_expenses.expense_count} entries)</td>
                <td className="px-6 py-2 text-right text-sm font-semibold text-[#1a1a2e]">{formatCurrency(operating_expenses.other_expenses_total)}</td>
                {comparison && <td className="px-6 py-2 text-right text-sm text-neutral-400">-</td>}
              </tr>
            )}
            <tr className="border-t border-red-200 bg-red-50/60">
              <td className="px-6 py-2.5 pl-10 text-sm font-bold text-red-800">Total Operating Expenses</td>
              <td className="px-6 py-2.5 text-right text-sm font-bold text-red-800">{formatCurrency(operating_expenses.total)}</td>
              {comparison && <td className="px-6 py-2.5 text-right text-sm text-neutral-500">{formatCurrency(comparison.salary_expense)}</td>}
            </tr>

            {/* NET PROFIT */}
            <tr className={`border-t-2 ${net_profit.amount >= 0 ? 'bg-emerald-100 border-emerald-300' : 'bg-red-100 border-red-300'}`}>
              <td className={`px-6 py-4 text-base font-bold ${net_profit.amount >= 0 ? 'text-emerald-900' : 'text-red-900'}`}>
                NET {net_profit.amount >= 0 ? 'PROFIT' : 'LOSS'} <span className="text-xs font-normal ml-1">({net_profit.margin}% margin)</span>
              </td>
              <td className={`px-6 py-4 text-right text-base font-bold ${net_profit.amount >= 0 ? 'text-emerald-900' : 'text-red-900'}`}>
                {formatCurrency(Math.abs(net_profit.amount))}
              </td>
              {comparison && (
                <td className="px-6 py-4 text-right text-sm font-medium text-neutral-500">
                  {formatCurrency(comparison.net_profit)}
                  {comparison.revenue_change !== 0 && (
                    <span className={`ml-1 text-xs ${comparison.revenue_change > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      ({comparison.revenue_change > 0 ? '+' : ''}{comparison.revenue_change}%)
                    </span>
                  )}
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Tax Info */}
      <div className="glass-card shadow-soft p-5">
        <h3 className="text-sm font-bold text-neutral-700 uppercase tracking-wide mb-3">Tax Information (Pass-Through)</h3>
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded-xl">
            <p className="text-xs text-blue-600 font-medium">GST Collected</p>
            <p className="text-lg font-bold text-blue-900">{formatCurrency(tax_info.gst_collected)}</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-xl">
            <p className="text-xs text-green-600 font-medium">GST Input Credit</p>
            <p className="text-lg font-bold text-green-900">{formatCurrency(tax_info.gst_on_purchases)}</p>
          </div>
          <div className="text-center p-3 bg-orange-50 rounded-xl">
            <p className="text-xs text-orange-600 font-medium">Net GST Payable</p>
            <p className="text-lg font-bold text-orange-900">{formatCurrency(tax_info.net_gst_payable)}</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-xl">
            <p className="text-xs text-purple-600 font-medium">TDS by Clients</p>
            <p className="text-lg font-bold text-purple-900">{formatCurrency(tax_info.tds_deducted_by_clients)}</p>
          </div>
        </div>
      </div>

      {/* Monthly Breakdown */}
      {monthly_breakdown && monthly_breakdown.length > 0 && (
        <div className="glass-card shadow-soft overflow-hidden">
          <div className="px-5 py-3 bg-white/40 border-b border-white/40">
            <h3 className="text-sm font-bold text-neutral-700 uppercase tracking-wide">Monthly Breakdown</h3>
          </div>
          <table className="w-full">
            <thead>
              <tr className="bg-white/40 border-b border-white/40">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-600">Month</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-600">Revenue</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-600">Credit Notes</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-600">Direct Costs</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-600">Salaries</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-600">Other Exp.</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-600">Net Profit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/30">
              {monthly_breakdown.map((m, i) => (
                <tr key={i} className="hover:bg-white/60">
                  <td className="px-4 py-2.5 text-sm font-medium text-[#1a1a2e]">{m.month}</td>
                  <td className="px-4 py-2.5 text-right text-sm text-emerald-700">{formatCurrency(m.revenue)}</td>
                  <td className="px-4 py-2.5 text-right text-sm text-red-500">{m.credit_notes > 0 ? `(${formatCurrency(m.credit_notes)})` : '-'}</td>
                  <td className="px-4 py-2.5 text-right text-sm text-orange-600">{m.direct_costs > 0 ? formatCurrency(m.direct_costs) : '-'}</td>
                  <td className="px-4 py-2.5 text-right text-sm text-neutral-600">{formatCurrency(m.salary_expense)}</td>
                  <td className="px-4 py-2.5 text-right text-sm text-neutral-600">{m.other_expenses > 0 ? formatCurrency(m.other_expenses) : '-'}</td>
                  <td className={`px-4 py-2.5 text-right text-sm font-bold ${m.net_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {formatCurrency(Math.abs(m.net_profit))}
                    {m.net_profit < 0 && <span className="text-xs ml-0.5">(Loss)</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============ BALANCE SHEET REPORT COMPONENT ============
function BalanceSheetReport({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [asOfDate, setAsOfDate] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (asOfDate) params.append('as_of_date', asOfDate);
      const response = await adminApi.get(`/api/admin/accounting/reports/balance-sheet?${params}`);
      setData(response.data);
    } catch (err) {
      toast.error('Failed to load Balance Sheet');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="balance-sheet-loading">
        <HeaderSkeleton withActions={false} />
        <MetricCardsSkeleton count={3} />
        <TableSkeleton rows={10} columns={3} />
      </div>
    );
  }

  if (!data) return null;

  const { assets, liabilities, equity, summary } = data;
  const ar = assets.current_assets.accounts_receivable;
  const gst = liabilities.current_liabilities.gst_payable;

  return (
    <div data-testid="balance-sheet-report" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="bs-back-btn">
            <X className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h2 className="text-xl font-bold text-[#1a1a2e]">Balance Sheet</h2>
            <p className="text-sm text-neutral-500">As of {data.as_of_date}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={asOfDate} onChange={e => setAsOfDate(e.target.value)} className="w-40 text-sm" placeholder="As of" />
          <Button size="sm" onClick={fetchData} data-testid="bs-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Balance verification badge */}
      <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium ${summary.is_balanced ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
        {summary.is_balanced ? '= Balanced' : 'Imbalanced'}: Assets ({formatCurrency(summary.total_assets)}) = Liabilities + Equity ({formatCurrency(summary.total_liabilities_and_equity)})
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* ASSETS */}
        <div className="glass-card shadow-soft overflow-hidden">
          <div className="px-6 py-3 bg-blue-50 border-b border-blue-200">
            <h3 className="text-sm font-bold text-blue-800 uppercase tracking-wide">Assets</h3>
          </div>
          <table className="w-full">
            <tbody className="divide-y divide-white/30">
              <tr className="bg-blue-50/50">
                <td colSpan={2} className="px-6 py-2 text-xs font-bold text-blue-700 uppercase tracking-wider">Current Assets</td>
              </tr>
              <tr>
                <td className="px-6 py-2.5 pl-10 text-sm text-neutral-700">Accounts Receivable</td>
                <td className="px-6 py-2.5 text-right text-sm font-medium text-[#1a1a2e]">{formatCurrency(ar.total)}</td>
              </tr>
              <tr>
                <td className="px-6 py-1.5 pl-14 text-xs text-neutral-500">Current (not overdue)</td>
                <td className="px-6 py-1.5 text-right text-xs text-neutral-500">{formatCurrency(ar.current)}</td>
              </tr>
              <tr>
                <td className="px-6 py-1.5 pl-14 text-xs text-red-500">Overdue</td>
                <td className="px-6 py-1.5 text-right text-xs text-red-500">{formatCurrency(ar.overdue)}</td>
              </tr>
              <tr>
                <td className="px-6 py-2.5 pl-10 text-sm text-neutral-700">TDS Receivable</td>
                <td className="px-6 py-2.5 text-right text-sm font-medium text-[#1a1a2e]">{formatCurrency(assets.current_assets.tds_receivable)}</td>
              </tr>
              <tr>
                <td className="px-6 py-2.5 pl-10 text-sm text-neutral-700">Cash & Bank</td>
                <td className="px-6 py-2.5 text-right text-sm font-medium text-[#1a1a2e]">{formatCurrency(assets.current_assets.cash_and_bank)}</td>
              </tr>
              <tr className="bg-blue-100 border-t-2 border-blue-300">
                <td className="px-6 py-3 text-sm font-bold text-blue-900">TOTAL ASSETS</td>
                <td className="px-6 py-3 text-right text-base font-bold text-blue-900">{formatCurrency(assets.total_assets)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* LIABILITIES & EQUITY */}
        <div className="glass-card shadow-soft overflow-hidden">
          <div className="px-6 py-3 bg-red-50 border-b border-red-200">
            <h3 className="text-sm font-bold text-red-800 uppercase tracking-wide">Liabilities & Equity</h3>
          </div>
          <table className="w-full">
            <tbody className="divide-y divide-white/30">
              <tr className="bg-red-50/50">
                <td colSpan={2} className="px-6 py-2 text-xs font-bold text-red-700 uppercase tracking-wider">Current Liabilities</td>
              </tr>
              <tr>
                <td className="px-6 py-2.5 pl-10 text-sm text-neutral-700">GST Payable</td>
                <td className="px-6 py-2.5 text-right text-sm font-medium text-[#1a1a2e]">{formatCurrency(gst.total)}</td>
              </tr>
              <tr>
                <td className="px-6 py-1.5 pl-14 text-xs text-neutral-500">Collected: {formatCurrency(gst.gst_collected)}</td>
                <td className="px-6 py-1.5 text-right text-xs text-neutral-500">Input: ({formatCurrency(gst.input_credit)})</td>
              </tr>
              <tr>
                <td className="px-6 py-2.5 pl-10 text-sm text-neutral-700">Accounts Payable</td>
                <td className="px-6 py-2.5 text-right text-sm font-medium text-[#1a1a2e]">{formatCurrency(liabilities.current_liabilities.accounts_payable)}</td>
              </tr>
              <tr>
                <td className="px-6 py-2.5 pl-10 text-sm text-neutral-700">Unapplied Credit Notes</td>
                <td className="px-6 py-2.5 text-right text-sm font-medium text-[#1a1a2e]">{formatCurrency(liabilities.current_liabilities.unapplied_credit_notes)}</td>
              </tr>
              {liabilities.current_liabilities.accrued_expenses > 0 && (
                <tr>
                  <td className="px-6 py-2.5 pl-10 text-sm text-neutral-700">Accrued Expenses <span className="text-xs text-neutral-400">(Approved, unpaid)</span></td>
                  <td className="px-6 py-2.5 text-right text-sm font-medium text-[#1a1a2e]">{formatCurrency(liabilities.current_liabilities.accrued_expenses)}</td>
                </tr>
              )}
              <tr className="bg-red-100 border-t border-red-200">
                <td className="px-6 py-2.5 text-sm font-bold text-red-800">Total Liabilities</td>
                <td className="px-6 py-2.5 text-right text-sm font-bold text-red-800">{formatCurrency(liabilities.total_liabilities)}</td>
              </tr>
              <tr className="bg-indigo-50/50">
                <td colSpan={2} className="px-6 py-2 text-xs font-bold text-indigo-700 uppercase tracking-wider">Equity</td>
              </tr>
              <tr>
                <td className="px-6 py-2.5 pl-10 text-sm text-neutral-700">Retained Earnings</td>
                <td className="px-6 py-2.5 text-right text-sm font-medium text-[#1a1a2e]">{formatCurrency(equity.retained_earnings)}</td>
              </tr>
              <tr className="bg-indigo-100 border-t-2 border-indigo-300">
                <td className="px-6 py-3 text-sm font-bold text-indigo-900">TOTAL LIABILITIES + EQUITY</td>
                <td className="px-6 py-3 text-right text-base font-bold text-indigo-900">{formatCurrency(summary.total_liabilities_and_equity)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Metadata */}
      <div className="text-center text-xs text-neutral-400">
        Based on {data.metadata.total_invoices} invoices, {data.metadata.total_pos} purchase orders, {data.metadata.total_credit_notes} credit notes
      </div>
    </div>
  );
}



// Main Reports Component
// ============ GST & TDS INTERNAL TRACKER ============
function GSTTDSInternalReport({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState('current_fy');
  const defaultRange = getReportDateRange('current_fy');
  const [fromDate, setFromDate] = useState(defaultRange.from);
  const [toDate, setToDate] = useState(defaultRange.to);
  const [gstFilter, setGstFilter] = useState('');
  const [tdsFilter, setTdsFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      const range = getReportDateRange(preset);
      setFromDate(range.from);
      setToDate(range.to);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (gstFilter) params.gst_status = gstFilter;
      if (tdsFilter) params.tds_status = tdsFilter;
      const res = await adminApi.get('/api/admin/accounting/reports/gst-tds-internal', { params });
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load GST/TDS data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [fromDate, toDate]);

  const handleApplyFilters = () => fetchData();
  const handleClearFilters = () => {
    const range = getReportDateRange('current_fy');
    setDatePreset('current_fy');
    setFromDate(range.from); setToDate(range.to); setGstFilter(''); setTdsFilter(''); setSearchQuery('');
    setTimeout(fetchData, 0);
  };

  const handleUpdateFiling = async (invoiceId, field, value) => {
    try {
      await adminApi.patch(`/api/admin/accounting/invoices/${invoiceId}/filing-status`, { [field]: value });
      setData(prev => ({
        ...prev,
        invoices: prev.invoices.map(inv => inv.id === invoiceId ? { ...inv, [field]: value } : inv)
      }));
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);

  const filteredInvoices = (data?.invoices || []).filter(inv => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (inv.invoice_number || '').toLowerCase().includes(q) || (inv.client_name || '').toLowerCase().includes(q);
  });

  const m = data?.metrics || {};

  return (
    <div className="space-y-6" data-testid="gst-tds-internal-report">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 hover:bg-neutral-100 rounded-xl">
            <X className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-[#1a1a2e]">GST & TDS Filing Tracker</h2>
            <p className="text-sm text-neutral-500">Internal tracking only — does not affect calculations</p>
          </div>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-gst-tds-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Metric Cards */}
      {!loading && data && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="gst-tds-metrics">
          <div className="bg-white border rounded-xl p-3">
            <p className="text-xs text-neutral-500 font-medium">Total Invoices</p>
            <p className="text-xl font-bold text-[#1a1a2e]">{m.total_invoices || 0}</p>
          </div>
          <div className="bg-white/40 border border-white/40 rounded-xl p-3">
            <p className="text-xs text-neutral-500 font-medium">GST Pending</p>
            <p className="text-lg font-bold text-neutral-600">{m.gst_pending?.count || 0}</p>
            <p className="text-xs text-neutral-400">{formatCurrency(m.gst_pending?.gst_value)}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <p className="text-xs text-blue-600 font-medium">GST Filed</p>
            <p className="text-lg font-bold text-blue-700">{m.gst_filed?.count || 0}</p>
            <p className="text-xs text-blue-400">{formatCurrency(m.gst_filed?.gst_value)}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-3">
            <p className="text-xs text-green-600 font-medium">GST Paid</p>
            <p className="text-lg font-bold text-green-700">{m.gst_paid?.count || 0}</p>
            <p className="text-xs text-green-400">{formatCurrency(m.gst_paid?.gst_value)}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-3">
            <p className="text-xs text-green-600 font-medium">TDS Paid</p>
            <p className="text-lg font-bold text-green-700">{m.tds_paid?.count || 0}</p>
            <p className="text-xs text-green-400">{formatCurrency(m.tds_paid?.tds_value)}</p>
          </div>
          <div className="bg-white/40 border border-white/40 rounded-xl p-3">
            <p className="text-xs text-neutral-500 font-medium">TDS Not Paid</p>
            <p className="text-lg font-bold text-neutral-600">{m.tds_not_paid?.count || 0}</p>
            <p className="text-xs text-neutral-400">{formatCurrency(m.tds_not_paid?.tds_value)}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border rounded-xl p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs text-neutral-500 font-medium block mb-1">Period</label>
            <DatePresetSelector value={datePreset} onChange={handlePresetChange} />
          </div>
          {datePreset === 'custom' && (
            <>
            <div>
              <label className="text-xs text-neutral-500 font-medium block mb-1">From</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="px-2 py-1.5 text-sm border rounded" data-testid="gst-tds-from-date" />
            </div>
            <div>
              <label className="text-xs text-neutral-500 font-medium block mb-1">To</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="px-2 py-1.5 text-sm border rounded" data-testid="gst-tds-to-date" />
            </div>
            </>
          )}
          <div>
            <label className="text-xs text-neutral-500 font-medium block mb-1">GST Status</label>
            <SearchableSelect
              value={gstFilter}
              onChange={setGstFilter}
              placeholder="All"
              triggerClassName="h-9 min-w-[120px] text-sm rounded"
              options={[
                { value: '', label: 'All' },
                { value: 'pending', label: 'Pending' },
                { value: 'filed', label: 'Filed' },
                { value: 'paid', label: 'Paid' },
              ]}
              data-testid="gst-status-filter"
            />
          </div>
          <div>
            <label className="text-xs text-neutral-500 font-medium block mb-1">TDS Status</label>
            <SearchableSelect
              value={tdsFilter}
              onChange={setTdsFilter}
              placeholder="All"
              triggerClassName="h-9 min-w-[120px] text-sm rounded"
              options={[
                { value: '', label: 'All' },
                { value: 'not_paid', label: 'Not Paid' },
                { value: 'paid', label: 'Paid' },
              ]}
              data-testid="tds-status-filter"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-neutral-500 font-medium block mb-1">Search</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2 text-neutral-400" />
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Invoice # or client..." className="pl-8 pr-2 py-1.5 text-sm border rounded w-full" data-testid="gst-tds-search" />
            </div>
          </div>
          <Button onClick={handleApplyFilters} size="sm" data-testid="apply-gst-tds-filters">
            <Filter className="w-3 h-3 mr-1" /> Apply
          </Button>
          <Button onClick={handleClearFilters} variant="outline" size="sm">
            <X className="w-3 h-3 mr-1" /> Clear
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-neutral-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/40 border-b">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-neutral-600">Invoice #</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-neutral-600">Client</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-neutral-600">Date</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-neutral-600">Subtotal</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-neutral-600">GST</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-neutral-600">TDS</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-neutral-600">Total</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-neutral-600">Status</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-neutral-600">GST Filing</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-neutral-600">TDS Filing</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredInvoices.length === 0 ? (
                  <tr><td colSpan="10" className="px-4 py-2">
                    <EmptyState icon={Receipt} title="No invoices found" description="Try adjusting your filters." testId="empty-gst-tds-table" />
                  </td></tr>
                ) : filteredInvoices.map(inv => (
                  <tr key={inv.id} className="hover:bg-white/60" data-testid={`gst-tds-row-${inv.id}`}>
                    <td className="px-3 py-2.5 text-sm font-medium max-w-[130px] truncate">{inv.invoice_number}</td>
                    <td className="px-3 py-2.5 text-sm truncate max-w-[180px]" title={inv.client_name}>{inv.client_name}</td>
                    <td className="px-3 py-2.5 text-sm text-neutral-600">{inv.invoice_date || '-'}</td>
                    <td className="px-3 py-2.5 text-sm text-right">{formatCurrency(inv.subtotal)}</td>
                    <td className="px-3 py-2.5 text-sm text-right">{formatCurrency(inv.total_gst)}</td>
                    <td className="px-3 py-2.5 text-sm text-right">{formatCurrency(inv.tds_amount)}</td>
                    <td className="px-3 py-2.5 text-sm text-right font-medium">{formatCurrency(inv.total)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium capitalize ${
                        inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                        inv.status === 'overdue' ? 'bg-red-100 text-red-700' :
                        inv.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                        'bg-neutral-100 text-neutral-700'
                      }`}>{inv.status?.replace('_', ' ')}</span>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <SearchableSelect
                        value={inv.gst_filing_status || 'pending'}
                        onChange={(v) => handleUpdateFiling(inv.id, 'gst_filing_status', v)}
                        triggerClassName={`h-7 text-[10px] font-semibold uppercase px-2 rounded border-0 shadow-none ${
                          inv.gst_filing_status === 'paid' ? 'bg-green-100 text-green-700 hover:bg-green-100' :
                          inv.gst_filing_status === 'filed' ? 'bg-blue-100 text-blue-700 hover:bg-blue-100' :
                          'bg-neutral-100 text-neutral-500 hover:bg-neutral-100'
                        }`}
                        options={[
                          { value: 'pending', label: 'PENDING' },
                          { value: 'filed', label: 'FILED' },
                          { value: 'paid', label: 'PAID' },
                        ]}
                        data-testid={`gst-filing-select-${inv.id}`}
                      />
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <SearchableSelect
                        value={inv.tds_filing_status || 'not_paid'}
                        onChange={(v) => handleUpdateFiling(inv.id, 'tds_filing_status', v)}
                        triggerClassName={`h-7 text-[10px] font-semibold uppercase px-2 rounded border-0 shadow-none ${
                          inv.tds_filing_status === 'paid' ? 'bg-green-100 text-green-700 hover:bg-green-100' :
                          'bg-neutral-100 text-neutral-500 hover:bg-neutral-100'
                        }`}
                        options={[
                          { value: 'not_paid', label: 'NOT PAID' },
                          { value: 'paid', label: 'PAID' },
                        ]}
                        data-testid={`tds-filing-select-${inv.id}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


export default function AccountingReports({ initialReport, onNavigate }) {
  const [activeReport, setActiveReport] = useState(initialReport || null);

  // Update active report when initialReport prop changes
  useEffect(() => {
    if (initialReport) {
      setActiveReport(initialReport);
    }
  }, [initialReport]);

  const reports = [
    {
      id: 'profit-loss',
      title: 'Profit & Loss',
      description: 'Income statement with revenue, costs, and net profit',
      icon: TrendingUp,
      color: 'emerald'
    },
    {
      id: 'balance-sheet',
      title: 'Balance Sheet',
      description: 'Assets, liabilities, and equity snapshot',
      icon: BarChart2,
      color: 'indigo'
    },
    {
      id: 'revenue',
      title: 'Revenue Report',
      description: 'Income breakdown by period, client, or invoice type',
      icon: TrendingUp,
      color: 'blue'
    },
    {
      id: 'aged-receivables',
      title: 'Aged Receivables',
      description: 'Outstanding amounts grouped by age buckets',
      icon: Clock,
      color: 'orange'
    },
    {
      id: 'client-statement',
      title: 'Client Statement',
      description: 'Transaction ledger for a specific client',
      icon: Users,
      color: 'purple'
    },
    {
      id: 'gst-summary',
      title: 'GST Summary',
      description: 'GSTR-1/GSTR-3B preparation summary',
      icon: Receipt,
      color: 'teal'
    },
    {
      id: 'tds-summary',
      title: 'TDS Summary',
      description: 'TDS deduction details for compliance',
      icon: FileText,
      color: 'green'
    },
    {
      id: 'gst-tds-internal',
      title: 'GST & TDS Internal',
      description: 'Track GST filing and TDS payment status across all invoices',
      icon: Shield,
      color: 'pink'
    }
  ];

  // Handle active report with potential initial parameters
  if (activeReport === 'profit-loss') {
    return <ProfitLossReport onBack={() => setActiveReport(null)} />;
  }
  if (activeReport === 'balance-sheet') {
    return <BalanceSheetReport onBack={() => setActiveReport(null)} />;
  }
  if (activeReport === 'revenue') {
    return <RevenueReport onBack={() => setActiveReport(null)} />;
  }
  if (activeReport === 'aged-receivables') {
    return <AgedReceivablesReport onBack={() => setActiveReport(null)} />;
  }
  if (activeReport?.startsWith('client-statement')) {
    const clientId = activeReport.split(':')[1] || null;
    return <ClientStatementReport onBack={() => setActiveReport(null)} initialClientId={clientId} />;
  }
  if (activeReport === 'gst-summary') {
    return <GSTSummaryReport onBack={() => setActiveReport(null)} />;
  }
  if (activeReport === 'tds-summary') {
    return <TDSSummaryReport onBack={() => setActiveReport(null)} />;
  }
  if (activeReport === 'gst-tds-internal') {
    return <GSTTDSInternalReport onBack={() => setActiveReport(null)} />;
  }

  return (
    <div className="space-y-6" data-testid="accounting-reports">
      <div>
        <h1 className="text-2xl font-bold text-[#1a1a2e]">Financial Reports</h1>
        <p className="text-sm text-neutral-500 mt-1">Generate and view detailed financial reports</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reports.map(report => (
          <ReportTypeCard
            key={report.id}
            {...report}
            onClick={() => setActiveReport(report.id)}
          />
        ))}
      </div>
    </div>
  );
}
